export type ScrollSplit={pageFirst:number;column:number;pageLast:number};
export function splitBoardScroll(delta:number,pageGap:number,columnTop:number,columnMax:number):ScrollSplit {
  if(delta>0){
    const pageFirst=Math.min(delta,Math.max(0,pageGap));
    const column=Math.min(delta-pageFirst,Math.max(0,columnMax-columnTop));
    return {pageFirst,column,pageLast:delta-pageFirst-column};
  }
  const column=-Math.min(-delta,Math.max(0,columnTop));
  return {pageFirst:0,column,pageLast:delta-column};
}

// Keep one destination for the whole wheel stream; ease its remaining distance
// each frame instead of starting separate animations for the page and column.
export function createScrollStream<T>(move:(delta:number,target:T)=>void,requestFrame:(callback:FrameRequestCallback)=>number,cancelFrame:(id:number)=>void){
  let pending=0,frame=0,lastTime=0,target:T;
  const tick=(time:number)=>{
    frame=0;
    const elapsed=lastTime?Math.min(32,Math.max(1,time-lastTime)):16;lastTime=time;
    const amount=Math.abs(pending)<.5?pending:pending*(1-Math.exp(-elapsed/45));
    pending-=amount;move(amount,target);
    if(pending)frame=requestFrame(tick);else lastTime=0;
  };
  return {
    push(delta:number,nextTarget:T){
      if(!delta)return;
      // A reversal or a different column should respond immediately.
      if(target!==nextTarget||pending*delta<0)pending=0;
      target=nextTarget;pending+=delta;
      if(!frame)frame=requestFrame(tick);
    },
    stop(){if(frame)cancelFrame(frame);frame=0;pending=0;lastTime=0}
  };
}

export function attachBoardScroll(shell:HTMLElement,topbar:HTMLElement|null){
  let fractional=0,previousColumn:HTMLElement|null=null;
  const stream=createScrollStream<HTMLElement>((delta,column)=>{
    if(!column.isConnected)return;
    if(previousColumn!==column){fractional=0;previousColumn=column}
    const precise=delta+fractional;delta=Math.trunc(precise);fractional=precise-delta;
    if(!delta)return;
    const page=document.scrollingElement||document.documentElement;
    const offset=Math.max(0,topbar?.getBoundingClientRect().bottom||0)+8;
    const pageGap=Math.min(Math.max(0,shell.getBoundingClientRect().top-offset),Math.max(0,page.scrollHeight-window.innerHeight-window.scrollY));
    const steps=splitBoardScroll(delta,pageGap,column.scrollTop,column.scrollHeight-column.clientHeight);
    if(steps.pageFirst)window.scrollBy({top:steps.pageFirst,behavior:'instant'});
    column.scrollTop+=steps.column;
    if(steps.pageLast)window.scrollBy({top:steps.pageLast,behavior:'instant'});
  },requestAnimationFrame,cancelAnimationFrame);
  const wheel=(event:WheelEvent)=>{
    if(event.ctrlKey||event.shiftKey||Math.abs(event.deltaX)>Math.abs(event.deltaY)||!event.deltaY||!event.cancelable)return;
    const target=event.target instanceof Element?event.target:null;
    if(target?.closest('textarea,select,input,[contenteditable="true"]'))return;
    const column=target?.closest('.task-column')?.querySelector<HTMLElement>(':scope > div');
    if(!column)return;
    event.preventDefault();
    stream.push(event.deltaY*(event.deltaMode===1?16:event.deltaMode===2?column.clientHeight:1),column);
  };
  const stop=()=>stream.stop();
  shell.addEventListener('wheel',wheel,{passive:false});
  shell.addEventListener('pointerdown',stop,{passive:true});
  window.addEventListener('keydown',stop);
  return()=>{stream.stop();shell.removeEventListener('wheel',wheel);shell.removeEventListener('pointerdown',stop);window.removeEventListener('keydown',stop)};
}
